import React, { memo, ReactNode, useMemo } from 'react';
import { Box, Divider, Grid, Link, Theme, Typography, useMediaQuery, Zoom } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { formatDateTime } from 'utils/format';
import { routes } from 'utils/routing';
import { getDisabledEventStatus, MARKETS_SLUGS_MAP } from 'utils/transform';
import sortParentsAndChildren from 'utils/sortParentsAndChildren';
import { CountOfCategories } from 'utils/constants';
import { Event } from 'api/general/models';

import Flag from 'components/common/Flag';
import Label from 'components/common/Label';
import Image from 'components/common/Image';
import TextTransformer from 'components/common/TextTransformer';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import usePlatform from 'hooks/usePlatform';
import { CreatingOrderSelectors, DetectMarketSelectors } from '../../store/selectors';
import useCustomOrgShowTime from '../../hooks/useCustomOrgShowTime';

interface EventInfoProps {
  secondary?: boolean;
  event: Event;
  imagePopUp?: ReactNode;
  isPopUpToggled?: boolean;
  ageTag?: boolean;
  categoryTag?: boolean;
  dateTimeTag?: boolean;
  multipleTimeTag?: boolean;
  disabled?: boolean;
  isMainPage?: boolean;
  children?: ReactNode;
  share?: ReactNode;
  like?: ReactNode;
  onMainImageClick?: (image: any) => void;
}

interface StyleProps {
  dontBrake: boolean;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  container: {
    ...theme.mixins.scrollBarOverflowYAuto,
    overflowY: 'auto',
    color: theme.palette.custom.white,
    background: `linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 
    100%), ${theme.palette.background.default};`,
    backgroundBlendMode: 'soft-light, normal',
    [theme.breakpoints.up('md')]: {
      height: '100%',
      marginTop: -12,
    },
    '&.secondary': {
      color: theme.palette.text.primary,
      [theme.breakpoints.down('md')]: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'stretch',
      },
    },
  },
  cursor: {
    cursor: 'pointer',
  },
  imageContainer: {
    background: theme.palette.background.default,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    '.secondary &': {
      [theme.breakpoints.down('md')]: {
        width: 104,
        overflow: 'hidden',
        margin: theme.spacing(3, 0),
        '& img': {
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
        },
      },
    },
  },
  contentContainer: {
    flexShrink: 0,
    padding: theme.spacing(4, 4, 0),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4, 2, 0),
    },
    '.secondary &': {
      padding: theme.spacing(3, 4, 4),
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(3, 2),
        width: 'calc(100% - 104px)',
        justifyContent: 'space-between',
      },
    },
  },
  childrenContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 0,
    padding: theme.spacing(0, 4, 4),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 2, 4),
    },
    '.secondary &': {
      [theme.breakpoints.down('md')]: {
        padding: 0,
      },
    },
    '& > *:last-child': {
      flex: 1,
    },
  },
  title: {
    marginLeft: 8,
    ...(!props?.dontBrake && {
      wordBreak: 'break-word',
    }),
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      ...theme.typography.h5,
    },
    '.secondary &': {
      ...theme.typography.h5,
      color: theme.palette.text.primary,
      [theme.breakpoints.down('md')]: {
        ...theme.typography.subtitle2,
        marginBottom: theme.spacing(1),
      },
    },
  },
  hosts: {
    marginBottom: 5,
    marginLeft: 8,
  },
  organizationProfile: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  labelContainer: {
    '&:not(:empty)': {
      margin: theme.spacing(-0.25),
      lineHeight: 0,
      '& > *': {
        margin: theme.spacing(0.25),
        '& > .MuiGrid-item': {
          padding: 4,
        },
      },
    },
  },
  divider: {
    margin: theme.spacing(2, 1),
    backgroundColor: theme.palette.custom.border.gray,
    '.secondary &': {
      [theme.breakpoints.down('md')]: {
        margin: theme.spacing(0, 2, 2),
      },
    },
  },
  fabContainer: {
    display: 'flex',
    position: 'absolute',
    zIndex: 1,
    alignItems: 'center',
    height: 0,
    bottom: 0,
    right: theme.spacing(4),
  },
  shadow: {
    position: 'relative',
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      background: alpha('#000', 0.6),
    },
  },
  imagePopUpContainer: {
    position: 'absolute',
    zIndex: 1,
    width: '100%',
    height: '100%',
  },
}));

const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const isTestEnv = () => BUILD_ENV === 'test';
const isStageEnv = () => BUILD_ENV === 'stage';

const DONT_BRAKE_TITLE_EVENTS = isTestEnv() || isStageEnv() ? [837] : [6246];

const EventInfo: React.FC<React.PropsWithChildren<EventInfoProps>> = ({
  ageTag,
  categoryTag,
  dateTimeTag,
  multipleTimeTag,
  children,
  like,
  share,
  onMainImageClick,
  isPopUpToggled,
  imagePopUp,
  secondary = false,
  disabled = false,
  isMainPage = false,
  event: {
    id,
    title,
    start_at,
    timezone,
    categories,
    target_ages,
    types,
    sessions,
    images = [],
    organization,
    organization_mentions = [],
    status_display,
    tickets_sale_status,
    hide_date,
    hide_time,
  },
}) => {
  const { classes, cx } = useStyles({ dontBrake: DONT_BRAKE_TITLE_EVENTS.includes(id) });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [t] = useTranslation('public');
  const router = useRouter();
  const platform = usePlatform();
  // `?platform=` only survives while the URL carries it; most checkout steps navigate via
  // `routes.eventOrder*(eventId)` without re-appending it, so the Redux flag (set once when the
  // cart is initialized and stable across the whole checkout flow) is the reliable fallback.
  const isEmbeddedCart = useSelector(CreatingOrderSelectors.getIsEmbedded);
  const linksDisabled = Boolean(platform) || isEmbeddedCart;
  const disabledStatus = getDisabledEventStatus(tickets_sale_status, status_display);
  const sliceOfCategories = categoryTag && categories.length !== 0 && categories.slice(0, CountOfCategories);
  const { customShowTime } = useCustomOrgShowTime(organization.id);
  const dateTimeTagLabel = useMemo(() => {
    if (hide_date && hide_time) return '';
    return dateTimeTag && start_at && formatDateTime(start_at, timezone, !hide_date, !hide_time && customShowTime);
  }, [customShowTime, dateTimeTag, hide_date, hide_time, start_at, timezone]);

  const multipleTimeTagLabel =
    multipleTimeTag && Array.isArray(sessions) && sessions.length > 1 && t('common:general.multipleTimes');
  const mainImage = images.find(({ main }: any) => main);
  const hosts = [{ organization }, ...organization_mentions];
  const market = useSelector(DetectMarketSelectors.getSelectedMarket);
  const variant = isMobile ? 'square' : 'rectangle';

  let handleImageClick;
  if (onMainImageClick) {
    handleImageClick = () => {
      onMainImageClick(mainImage);
    };
  }

  return (
    <Grid
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      className={cx(classes.container, { secondary })}
      container
      direction="column"
      wrap="nowrap"
    >
      {mainImage && (
        <Grid
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          className={classes.imageContainer}
          item
        >
          <Zoom in={isPopUpToggled}>
            <div
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.imagePopUpContainer}
            >
              {imagePopUp}
            </div>
          </Zoom>
          <Box
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className={cx({ [classes.cursor]: onMainImageClick, [classes.shadow]: disabledStatus })}
            onClick={handleImageClick}
          >
            <Image
              src={mainImage.image.original_url}
              alt={mainImage.image.filename}
              variant={isMainPage ? 'rectangle' : variant}
              priority
            />
            {disabledStatus && (
              <Flag
                text={t(`event.eventStatusDisplay`, { context: disabledStatus.title })}
                color={disabledStatus.color}
              />
            )}
          </Box>
          {(share || like) && (
            <div
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.fabContainer}
            >
              <Grid container spacing={2}>
                {like && <Grid item>{like}</Grid>}
                {share && <Grid item>{share}</Grid>}
              </Grid>
            </div>
          )}
        </Grid>
      )}
      <Grid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        className={classes.contentContainer}
        item
        container
        direction="column"
        wrap="nowrap"
      >
        <Grid item>
          <TextTransformer
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className={classes.title}
            limit={100}
            defaultFontSize="h4"
            minFontSize="h5"
            transformAfter={50}
            component="h1"
          >
            {title}
          </TextTransformer>
          <Typography
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className={classes.hosts}
            variant="body2"
            color="textPrimary"
          >
            {t<string>('event.eventInfo.hostedBy')}
            {hosts.map((host, index) => (
              <span key={host.organization.id}>
                {' '}
                <Link
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  className={classes.organizationProfile}
                  underline="none"
                  {...(() => {
                    if (disabled) return { component: 'span', color: 'textSecondary' };
                    if (linksDisabled) return { component: 'span' };
                    return {
                      target: '_blank',
                      href: routes.organizationProfile(host.organization.id).link,
                    };
                  })()}
                >
                  {host.organization.title}
                </Link>
                {index < hosts.length - 1 && ','}
              </span>
            ))}
          </Typography>
        </Grid>
        <Grid
          item
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          className={classes.labelContainer}
        >
          <Grid container style={{ marginLeft: 8 }}>
            {types &&
              sortParentsAndChildren(types).map((type) => (
                <Grid item key={type.id}>
                  <Label
                    key={type.id}
                    color="primary"
                    textColor="white"
                    size="small"
                    label={type.title}
                    {...(!linksDisabled &&
                      !disabled && {
                        hasHover: true,
                        onClick: () =>
                          router.push(
                            routes.events({
                              type: type.id,
                              market_slug: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP],
                            }).href,
                          ),
                      })}
                  />
                </Grid>
              ))}
            {sliceOfCategories &&
              sliceOfCategories.map((category) => (
                <Grid item>
                  <Label
                    key={category.id}
                    color="green"
                    textColor="white"
                    size="small"
                    label={category.title}
                    {...(!linksDisabled &&
                      !disabled && {
                        hasHover: true,
                        onClick: () =>
                          router.push(
                            routes.events({
                              category: category.id,
                              market_slug: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP],
                            }).href,
                          ),
                      })}
                  />
                </Grid>
              ))}
            {dateTimeTagLabel && (!hide_date || !hide_time) && (
              <Grid item>
                <Label color="primary" textColor="extraLightPrimary" size="small" label={dateTimeTagLabel} />
              </Grid>
            )}
            {multipleTimeTagLabel && (
              <Grid item>
                <Label color="primary" textColor="lightPrimary" size="small" label={multipleTimeTagLabel} />
              </Grid>
            )}
            {ageTag &&
              target_ages.map((targetAge: any) => (
                <Grid item>
                  <Label
                    color="purple"
                    textColor="white"
                    size="small"
                    label={targetAge?.title}
                    {...(!linksDisabled &&
                      !disabled && {
                        hasHover: true,
                        onClick: () =>
                          router.push(
                            routes.events({
                              target_age: targetAge?.id,
                              market_slug: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP],
                            }).href,
                          ),
                      })}
                  />
                </Grid>
              ))}
          </Grid>
        </Grid>
      </Grid>
      {children && (
        <Grid
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          className={classes.childrenContainer}
          item
        >
          {(!secondary || isMobile) && (
            <Divider
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.divider}
            />
          )}
          {children}
        </Grid>
      )}
    </Grid>
  );
};

export default memo(EventInfo);
